// Tenis10 match formats (RELEASE-TENIS10 § 11 P54 B1, Tudor 2026-10-25): the formats of tenis10.ro, chosen per competition for its
// groups and for its elimination phase, and the strict reading of a score against one. P92 (Tudor 2026-10-26, replacing P70): each
// tiebreak format is played either two points clear (the existing keys) or by the deciding point (its `…DecidingPoint` twin) — twelve.
//
// MIRROR: sportya-api `common/tenis10MatchFormat` — the same keys, defaults and rules; the API refuses what this refuses. A Tenis10
// competition carries `matchFormats: { groups, elimination }`, a match `competition.matchFormat` (the format it is played in).

// `tiebreakRule`: "twoClear" or "decidingPoint" for a tiebreak, null for games and a set.
const format = (key, kind, noOfSets, target, tiebreakRule = null) => Object.freeze({ key, kind, tiebreakRule, noOfSets, target })

export const TENIS10_MATCH_FORMATS = Object.freeze({
  tiebreak7: format("tiebreak7", "tiebreak", 1, 7, "twoClear"),
  tiebreak7DecidingPoint: format("tiebreak7DecidingPoint", "tiebreak", 1, 7, "decidingPoint"),
  tiebreak10: format("tiebreak10", "tiebreak", 1, 10, "twoClear"),
  tiebreak10DecidingPoint: format("tiebreak10DecidingPoint", "tiebreak", 1, 10, "decidingPoint"),
  bestOf3Tiebreaks7: format("bestOf3Tiebreaks7", "tiebreak", 3, 7, "twoClear"),
  bestOf3Tiebreaks7DecidingPoint: format("bestOf3Tiebreaks7DecidingPoint", "tiebreak", 3, 7, "decidingPoint"),
  bestOf3Tiebreaks10: format("bestOf3Tiebreaks10", "tiebreak", 3, 10, "twoClear"),
  bestOf3Tiebreaks10DecidingPoint: format("bestOf3Tiebreaks10DecidingPoint", "tiebreak", 3, 10, "decidingPoint"),
  bestOf3Games: format("bestOf3Games", "games", 1, 2),
  bestOf5Games: format("bestOf5Games", "games", 1, 3),
  bestOf7Games: format("bestOf7Games", "games", 1, 4),
  normalSet: format("normalSet", "set", 1, 6),
})
export const TENIS10_MATCH_FORMAT_KEYS = Object.keys(TENIS10_MATCH_FORMATS)
export const TENIS10_DEFAULT_MATCH_FORMATS = Object.freeze({ rosu: "tiebreak10", portocaliu: "tiebreak10", verde: "bestOf5Games", galben: "bestOf5Games" })

// The names the API sends with the editor's choices; the same, for a read that has none (the score form of a match). A twin shares
// its name; the rule tells them apart.
const NAMES = {
  tiebreak7: "Tiebreak to 7 points", tiebreak7DecidingPoint: "Tiebreak to 7 points", tiebreak10: "Tiebreak to 10 points",
  tiebreak10DecidingPoint: "Tiebreak to 10 points", bestOf3Tiebreaks7: "Best of 3 tiebreaks to 7 points",
  bestOf3Tiebreaks7DecidingPoint: "Best of 3 tiebreaks to 7 points", bestOf3Tiebreaks10: "Best of 3 tiebreaks to 10 points",
  bestOf3Tiebreaks10DecidingPoint: "Best of 3 tiebreaks to 10 points", bestOf3Games: "Best of 3 games", bestOf5Games: "Best of 5 games",
  bestOf7Games: "Best of 7 games", normalSet: "Normal set",
}
// A tiebreak's rule: two points clear, or the deciding point at one below the target all, named by its score (9-9 to 10).
const RULES = { twoClear: "win by 2 points", games: "deciding point at 40-40", set: "deciding point at 40-40, tiebreak at 6-6" }
const rule = ({ kind, tiebreakRule, target }) => tiebreakRule === "decidingPoint"
  ? `win by deciding point at ${target - 1}-${target - 1}` : RULES[tiebreakRule || kind]
export const matchFormatName = key => NAMES[key] || key
/** „Tiebreak to 10 points — win by deciding point at 9-9” (the R2 boards, P92: name, dash, rule). */
export const matchFormatLine = key => TENIS10_MATCH_FORMATS[key] ? `${NAMES[key]} — ${rule(TENIS10_MATCH_FORMATS[key])}` : ""

/** Both phases' formats a Tenis10 level plays by default. */
export const defaultMatchFormats = tenis10Level => TENIS10_DEFAULT_MATCH_FORMATS[tenis10Level]
  ? { groups: TENIS10_DEFAULT_MATCH_FORMATS[tenis10Level], elimination: TENIS10_DEFAULT_MATCH_FORMATS[tenis10Level] } : null

/** The format a match is played in: the one it was generated with, else its level's default; null for an adult match. */
export const matchFormatOf = match => {
  const competition = match?.competition
  if (!competition?.tenis10Level && !competition?.matchFormat) return null
  return TENIS10_MATCH_FORMATS[competition.matchFormat] || TENIS10_MATCH_FORMATS[TENIS10_DEFAULT_MATCH_FORMATS[competition.tenis10Level]] || null
}

const blank = value => value === undefined || value === null || value === ""
const toNumber = value => typeof value === "number" || (typeof value === "string" && value.trim() !== "") ? Number(value) : NaN
// A tiebreak played two points clear ends at its target two clear (10-8) or past it exactly two clear (11-9, 12-10; never 10-9 or
// 11-10); one played by the deciding point ends at its target, 10-9 included, and never goes past it.
const finishesSet = ({ kind, tiebreakRule, target }, won, lost) => {
  if (kind === "tiebreak" && tiebreakRule === "decidingPoint") return won === target
  if (kind === "tiebreak") return won >= target && (won === target ? won - lost >= 2 : won - lost === 2)
  if (kind === "games") return won === target
  return (won === 6 && lost <= 4) || (won === 7 && (lost === 5 || lost === 6))
}

/** One set against the format: { state: "empty" } (no numbers, or 0–0), "won" with its winner, or "invalid". */
export const readSet = (matchFormat, playerSet, opponentSet) => {
  if (blank(playerSet?.points) && blank(opponentSet?.points)) return { state: "empty" }
  const player = toNumber(playerSet?.points), opponent = toNumber(opponentSet?.points)
  if (![player, opponent].every(value => Number.isInteger(value) && value >= 0)) return { state: "invalid" }
  if (player === 0 && opponent === 0) return { state: "empty" }
  if (player === opponent) return { state: "invalid", player, opponent }
  const winner = player > opponent ? "player" : "opponent"
  const [won, lost] = player > opponent ? [player, opponent] : [opponent, player]
  return finishesSet(matchFormat, won, lost) ? { state: "won", winner, player, opponent } : { state: "invalid", player, opponent }
}

const setOf = (score, number) => Array.isArray(score) ? score.find(entry => Number(entry?.set) === number) : undefined

/** The strict reading of a whole score: complete (with `winner`), empty, incomplete or invalid (with `error: { code, set }`). */
export const evaluateScore = (matchFormat, playerScore, opponentScore) => {
  const sets = [], setsWon = { player: 0, opponent: 0 }
  const needed = Math.floor(matchFormat.noOfSets / 2) + 1
  let gap = null, decided = false
  const result = (status, error) => ({ status, winner: status === "complete" ? (setsWon.player > setsWon.opponent ? "player" : "opponent") : null,
    setsWon, sets, ...(error ? { error } : {}) })
  const extra = [playerScore, opponentScore].flatMap(score => Array.isArray(score) ? score : []).map(entry => Number(entry?.set))
    .filter(set => set > matchFormat.noOfSets)
  for (let number = 1; number <= matchFormat.noOfSets; number += 1) {
    const read = readSet(matchFormat, setOf(playerScore, number), setOf(opponentScore, number))
    if (read.state === "empty") gap = gap ?? number
    else if (decided) return result("invalid", { code: "TENIS10_SCORE_AFTER_DECIDED", set: number })
    else if (gap) return result("invalid", { code: "TENIS10_SCORE_SET_MISSING", set: gap })
    else if (read.state === "invalid") return result("invalid", { code: "TENIS10_SCORE_SET_INVALID", set: number })
    else {
      sets.push({ set: number, player: read.player, opponent: read.opponent, winner: read.winner })
      setsWon[read.winner] += 1
      decided = setsWon[read.winner] === needed
    }
  }
  const extraPlayed = extra.find(set => readSet(matchFormat, setOf(playerScore, set), setOf(opponentScore, set)).state !== "empty")
  if (extraPlayed) return result("invalid", { code: "TENIS10_SCORE_AFTER_DECIDED", set: extraPlayed })
  if (decided) return result("complete")
  if (!sets.length) return result("empty")
  return result("incomplete", { code: "TENIS10_SCORE_INCOMPLETE", set: gap ?? matchFormat.noOfSets })
}

const examples = ({ kind, tiebreakRule, target }) => {
  if (tiebreakRule === "decidingPoint") return `${target}–${target - 2}, ${target}–${target - 1}`
  if (kind === "tiebreak") return `${target}–${target - 2}, ${target + 1}–${target - 1}, ${target + 2}–${target}`
  return Array.from({ length: target }, (_, lost) => `${target}–${lost}`).join(", ")
}
const EXIT = "If the match stopped, choose Abandoned as the outcome of the child who retired."

/** Why the format does not finish a score (the Edit match alert); null when it does or when nothing is typed. */
export const scoreProblem = (matchFormat, playerScore, opponentScore) => {
  const reading = evaluateScore(matchFormat, playerScore, opponentScore)
  if (!["invalid", "incomplete"].includes(reading.status)) return null
  const { code, set } = reading.error
  const side = score => setOf(score, set)?.points
  if (code === "TENIS10_SCORE_INCOMPLETE") return `At one set all, tiebreak ${set} is played: enter it. ${EXIT}`
  if (code === "TENIS10_SCORE_AFTER_DECIDED") return `The match was over after tiebreak ${set - 1}: clear tiebreak ${set}.`
  if (code === "TENIS10_SCORE_SET_MISSING") return `Enter tiebreak ${set} before the next one.`
  if (blank(side(playerScore)) || blank(side(opponentScore))) return "Enter the score of both children."
  const score = `${side(playerScore)}–${side(opponentScore)}`
  if (matchFormat.kind === "tiebreak" && matchFormat.tiebreakRule === "decidingPoint") {
    const { target } = matchFormat
    const what = matchFormat.noOfSets === 1 ? `a tiebreak to ${target} points with a deciding point`
      : `tiebreak ${set} (to ${target} points, with a deciding point)`
    return `${score} does not finish ${what}: the first to ${target} points wins, with the deciding point at ${target - 1}–${target - 1} `
      + `(${examples(matchFormat)}). ${EXIT}`
  }
  if (matchFormat.kind === "tiebreak") {
    const what = matchFormat.noOfSets === 1 ? `a tiebreak to ${matchFormat.target} points` : `tiebreak ${set} (to ${matchFormat.target} points)`
    return `${score} does not finish ${what}: the winner needs at least ${matchFormat.target} points and 2 clear (${examples(matchFormat)}). ${EXIT}`
  }
  if (matchFormat.kind === "games") {
    return `${score} does not finish the ${NAMES[matchFormat.key].toLowerCase()}: the first to ${matchFormat.target} games wins (${examples(matchFormat)}). ${EXIT}`
  }
  return `${score} does not finish a normal set: 6–4 or better, 7–5 or 7–6. ${EXIT}`
}
